import { useEffect, useMemo, useState } from "react";
import AppShell from "../shell/AppShell.jsx";
import DetailDrawer from "../components/DetailDrawer.jsx";
import { useAgentPortalGuard } from "./useAgentPortalGuard.js";
import AgentPortalGuardState from "./AgentPortalGuardState.jsx";
import { useSosCasesList, SOS_CASES_DEFAULT_PAGE_SIZE } from "../sos/useSosCasesList.js";
import { getSosCaseDetail, acknowledgeSosCase, resolveSosCase } from "../sos/sosApi.js";
import { useAbnormalEventsList, ABNORMAL_EVENTS_DEFAULT_PAGE_SIZE } from "../abnormal/useAbnormalEventsList.js";
import { getAbnormalEventDetail, acknowledgeAbnormalEvent, resolveAbnormalEvent } from "../abnormal/abnormalApi.js";
import "./agentSosAbnormal.css";
import "../pages/states.css";
import Pagination from "../components/Pagination.jsx";

import PageSkeleton, { DetailSkeleton } from "../components/Skeleton.jsx";
const CRUMBS = ["Backoffice", "Agent", "SOS / Abnormal"];
const DRAWER_TITLE_ID = "asa-drawer-title";
const SEARCH_DEBOUNCE_MS = 350;

const STATUS_BADGE_VARIANT = { Open: "warning", Acknowledged: "info", Resolved: "success" };
const SEVERITY_BADGE_VARIANT = { High: "danger", Medium: "warning", Low: "neutral" };

function statusBadgeVariant(status) {
  return STATUS_BADGE_VARIANT[status] ?? "neutral";
}

function severityBadgeVariant(severity) {
  return SEVERITY_BADGE_VARIANT[severity] ?? "neutral";
}

function formatDateTimeTh(value) {
  if (!value) {
    return null;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  const datePart = date.toLocaleDateString("th-TH-u-ca-gregory", { day: "2-digit", month: "short", year: "numeric" });
  const timePart = date.toLocaleTimeString("th-TH", { hour: "2-digit", minute: "2-digit", hour12: false });
  return `${datePart} · ${timePart} ICT`;
}

function describeListError(error) {
  if (error?.status === 401) {
    return "เซสชันหมดอายุ กรุณาเข้าสู่ระบบใหม่";
  }
  return "กรุณาลองรีเฟรชหน้านี้อีกครั้ง หากยังพบปัญหากรุณาติดต่อฝ่ายปฏิบัติการ";
}

function describeWriteError(error) {
  if (error?.status === 403) {
    return "คุณไม่มีสิทธิ์ทำรายการนี้ เฉพาะ SuperAdmin หรือ Agent เจ้าของงานเท่านั้น";
  }
  if (error?.status === 409) {
    return "รายการนี้ถูกดำเนินการไปแล้วก่อนหน้านี้ กรุณารีเฟรชแล้วลองใหม่";
  }
  return "ไม่สามารถดำเนินการได้ กรุณาลองใหม่อีกครั้ง";
}

const TABS = [
  { key: "sos", label: "SOS" },
  { key: "abnormal", label: "Abnormal Heartbeat" },
];

/**
 * /backoffice/agent/sos-abnormal - Prompt 138's Agent Portal Safety screen: a
 * compact two-tab page (SOS / Abnormal Heartbeat) over the exact real Prompt
 * 124/116 backend APIs (../sos/sosApi.js, ../abnormal/abnormalApi.js)
 * unchanged - no new backend endpoint. Deliberately never renders the
 * "+ Manual Scan" action AbnormalHeartbeatPage.jsx has: ScanForMissingHeartbeatAsync
 * is SuperAdmin-only on the backend with no Agent branch (confirmed against
 * AbnormalHeartbeatService.cs), so this page never offers a control that
 * would only ever 403 for the Agent viewing it. Acknowledge/Resolve DO work
 * here - the backend allows the owning Agent to acknowledge/resolve their
 * own cases/events, gated per-row by the real nextActions.canAcknowledge/
 * canResolve the API itself returns, never a frontend guess.
 */
export default function AgentSosAbnormalPage() {
  const guard = useAgentPortalGuard();
  const [activeTab, setActiveTab] = useState("sos");

  if (guard.status !== "ready") {
    return <AgentPortalGuardState crumbs={CRUMBS} guard={guard} />;
  }

  return (
    <AppShell crumbs={CRUMBS}>
      <div className="wx-asa-page">
        <header className="wx-asa-head">
          <div>
            <h1>SOS / Abnormal ของฉัน</h1>
            <p>เคส SOS และเหตุการณ์แรงงานขาดสัญญาณในสายงานของคุณ การสั่งตรวจเองทำได้เฉพาะ SuperAdmin</p>
          </div>
        </header>

        <div className="wx-asa-tabs" role="tablist" aria-label="SOS หรือ Abnormal Heartbeat">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              type="button"
              role="tab"
              aria-selected={activeTab === tab.key}
              className={`wx-asa-tab${activeTab === tab.key ? " wx-asa-tab--active" : ""}`}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab === "sos" ? <SosTab guardReady={guard.status === "ready"} /> : <AbnormalTab guardReady={guard.status === "ready"} />}
      </div>
    </AppShell>
  );
}

function useDebouncedSearch(delayMs) {
  const [input, setInput] = useState("");
  const [value, setValue] = useState("");
  useEffect(() => {
    const timer = setTimeout(() => setValue(input), delayMs);
    return () => clearTimeout(timer);
  }, [input, delayMs]);
  return [input, setInput, value];
}

function SosTab({ guardReady }) {
  const [searchInput, setSearchInput, search] = useDebouncedSearch(SEARCH_DEBOUNCE_MS);
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState(null);
  const [detailState, setDetailState] = useState({ status: "idle", data: null, error: null, forId: null });
  const [actionState, setActionState] = useState({ status: "idle", error: null });

  useEffect(() => {
    setPage(1);
  }, [search, statusFilter]);

  const filters = useMemo(() => ({ page, pageSize: SOS_CASES_DEFAULT_PAGE_SIZE, search, caseStatus: statusFilter }), [page, search, statusFilter]);
  const { status: listStatus, data: listData, error: listError, refetch } = useSosCasesList(guardReady ? filters : null);

  useEffect(() => {
    if (!selectedId) {
      setDetailState({ status: "idle", data: null, error: null, forId: null });
      return undefined;
    }
    let cancelled = false;
    setDetailState({ status: "loading", data: null, error: null, forId: selectedId });
    getSosCaseDetail(selectedId)
      .then((data) => !cancelled && setDetailState({ status: "ready", data, error: null, forId: selectedId }))
      .catch((error) => !cancelled && setDetailState({ status: "error", data: null, error, forId: selectedId }));
    return () => {
      cancelled = true;
    };
  }, [selectedId]);

  function handleAcknowledge() {
    setActionState({ status: "submitting", error: null });
    acknowledgeSosCase(selectedId)
      .then((data) => {
        setActionState({ status: "idle", error: null });
        setDetailState({ status: "ready", data, error: null, forId: selectedId });
        refetch();
      })
      .catch((error) => setActionState({ status: "error", error }));
  }

  function handleResolve() {
    setActionState({ status: "submitting", error: null });
    resolveSosCase(selectedId)
      .then((data) => {
        setActionState({ status: "idle", error: null });
        setDetailState({ status: "ready", data, error: null, forId: selectedId });
        refetch();
      })
      .catch((error) => setActionState({ status: "error", error }));
  }

  const items = listData?.items ?? [];
  const totalCount = listData?.totalCount ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / SOS_CASES_DEFAULT_PAGE_SIZE));

  return (
    <>
      <div className="wx-asa-toolbar">
        <label className="wx-asa-search">
          <span className="wx-visually-hidden">ค้นหาเคส SOS</span>
          <input type="search" placeholder="ค้นหารหัสเคส, แรงงาน..." value={searchInput} onChange={(event) => setSearchInput(event.target.value)} />
        </label>
        <label className="wx-asa-filter">
          <span>สถานะ</span>
          <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
            <option value="">ทั้งหมด</option>
            <option value="Open">Open</option>
            <option value="Acknowledged">Acknowledged</option>
            <option value="Resolved">Resolved</option>
          </select>
        </label>
      </div>

      <section className="wx-panel wx-asa-list-panel">
        <div className="wx-asa-list-head">
          <h2 className="wx-panel-title">รายการเคส SOS</h2>
          <span className="wx-asa-list-count">{totalCount} เคส</span>
        </div>

        {listStatus === "error" && listData === null ? (
          <div className="wx-state-card wx-state-card--danger" role="alert">
            <p className="wx-state-body">{describeListError(listError)}</p>
          </div>
        ) : listStatus === "loading" && listData === null ? (
          <PageSkeleton variant="table" label="กำลังโหลดรายการ SOS..." />
        ) : items.length === 0 ? (
          <p className="wx-empty-list">ไม่พบเคส SOS ในสายงานของคุณ</p>
        ) : (
          <>
            <div className="wx-asa-table-wrap">
              <table className="wx-asa-table">
                <thead>
                  <tr>
                    <th>เคส</th>
                    <th>แรงงาน</th>
                    <th>ประเภท</th>
                    <th>ความรุนแรง</th>
                    <th>สถานะ</th>
                    <th>เปิดเมื่อ</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => (
                    <tr key={item.supportCaseId} className="wx-asa-row" onClick={() => setSelectedId(item.supportCaseId)}>
                      <td>
                        <strong className="wx-asa-cell-strong">{item.caseCode ?? "-"}</strong>
                      </td>
                      <td>{item.workerName ?? item.workerCode ?? "-"}</td>
                      <td>{item.sosType ?? "-"}</td>
                      <td>
                        <span className={`wx-badge wx-badge--${severityBadgeVariant(item.severity)}`}>{item.severity ?? "-"}</span>
                      </td>
                      <td>
                        <span className={`wx-badge wx-badge--${statusBadgeVariant(item.caseStatus)}`}>{item.caseStatus}</span>
                      </td>
                      <td>{formatDateTimeTh(item.openedAtUtc) ?? "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

              <Pagination
                page={page}
                totalPages={totalPages}
                onPageChange={setPage}
              />
          </>
        )}
      </section>

      <DetailDrawer open={selectedId !== null} onClose={() => setSelectedId(null)} labelledBy={DRAWER_TITLE_ID} selectableSelector="tr.wx-asa-row">
        <SosDetailContent
          titleId={DRAWER_TITLE_ID}
          state={detailState}
          actionState={actionState}
          onAcknowledge={handleAcknowledge}
          onResolve={handleResolve}
        />
      </DetailDrawer>
    </>
  );
}

function SosDetailContent({ titleId, state, actionState, onAcknowledge, onResolve }) {
  if (state.status === "loading" || state.status === "idle") {
    return (
      <DetailSkeleton label="กำลังโหลดรายละเอียด..." />
    );
  }
  if (state.status === "error") {
    return (
      <div className="wx-asa-drawer-state" role="alert">
        <h2 id={titleId} className="wx-state-title">
          ไม่สามารถโหลดรายละเอียดได้
        </h2>
      </div>
    );
  }

  const detail = state.data;

  return (
    <div className="wx-asa-drawer">
      <span className="wx-asa-drawer-kicker">SOS · DETAIL</span>
      <h2 id={titleId} className="wx-asa-drawer-title">
        {detail.caseCode ?? "-"}
      </h2>
      <p className="wx-asa-drawer-sub">{detail.workerName ?? detail.workerCode ?? "-"}</p>
      <div className="wx-asa-drawer-status">
        <span className={`wx-badge wx-badge--${statusBadgeVariant(detail.caseStatus)}`}>{detail.caseStatus}</span>
        <span className={`wx-badge wx-badge--${severityBadgeVariant(detail.severity)}`}>{detail.severity ?? "-"}</span>
      </div>

      <section className="wx-asa-dsec">
        <h3>รายละเอียด</h3>
        <div className="wx-asa-kv-row">
          <span>ประเภท</span>
          <strong>{detail.sosType ?? "-"}</strong>
        </div>
        <div className="wx-asa-kv-row">
          <span>นายจ้าง</span>
          <strong>{detail.employerName ?? "-"}</strong>
        </div>
        <div className="wx-asa-kv-row">
        </div>
        <div className="wx-asa-kv-row">
          <span>เปิดเมื่อ</span>
          <strong>{formatDateTimeTh(detail.openedAtUtc) ?? "-"}</strong>
        </div>
        {detail.acknowledgedAtUtc && (
          <div className="wx-asa-kv-row">
            <span>รับทราบเมื่อ</span>
            <strong>{formatDateTimeTh(detail.acknowledgedAtUtc)}</strong>
          </div>
        )}
        {detail.resolvedAtUtc && (
          <div className="wx-asa-kv-row">
            <span>ปิดเคสเมื่อ</span>
            <strong>{formatDateTimeTh(detail.resolvedAtUtc)}</strong>
          </div>
        )}
      </section>

      <section className="wx-asa-dsec">
        <h3>Timeline ({detail.eventTimeline.length})</h3>
        {detail.eventTimeline.length === 0 ? (
          <p className="wx-empty-list">ยังไม่มีเหตุการณ์</p>
        ) : (
          detail.eventTimeline.map((event) => (
            <div className="wx-asa-kv-row" key={event.sosEventId}>
              <span>{formatDateTimeTh(event.serverReceivedAtUtc) ?? "-"}</span>
              <strong>{event.message || event.sosType || "-"}</strong>
            </div>
          ))
        )}
      </section>

      {actionState.status === "error" && (
        <p className="wx-asa-form-message" role="alert">
          {describeWriteError(actionState.error)}
        </p>
      )}

      <div className="wx-asa-drawer-actions">
        {detail.nextActions.canAcknowledge && (
          <button type="button" className="wx-button wx-button--ghost" onClick={onAcknowledge} disabled={actionState.status === "submitting"}>
            รับทราบ (Acknowledge)
          </button>
        )}
        {detail.nextActions.canResolve && (
          <button type="button" className="wx-button wx-button--primary" onClick={onResolve} disabled={actionState.status === "submitting"}>
            ปิดเคส (Resolve)
          </button>
        )}
      </div>
    </div>
  );
}

function AbnormalTab({ guardReady }) {
  const [searchInput, setSearchInput, search] = useDebouncedSearch(SEARCH_DEBOUNCE_MS);
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState(null);
  const [detailState, setDetailState] = useState({ status: "idle", data: null, error: null, forId: null });
  const [actionState, setActionState] = useState({ status: "idle", error: null });

  useEffect(() => {
    setPage(1);
  }, [search, statusFilter]);

  const filters = useMemo(() => ({ page, pageSize: ABNORMAL_EVENTS_DEFAULT_PAGE_SIZE, search, status: statusFilter }), [page, search, statusFilter]);
  const { status: listStatus, data: listData, error: listError, refetch } = useAbnormalEventsList(guardReady ? filters : null);

  useEffect(() => {
    if (!selectedId) {
      setDetailState({ status: "idle", data: null, error: null, forId: null });
      return undefined;
    }
    let cancelled = false;
    setDetailState({ status: "loading", data: null, error: null, forId: selectedId });
    getAbnormalEventDetail(selectedId)
      .then((data) => !cancelled && setDetailState({ status: "ready", data, error: null, forId: selectedId }))
      .catch((error) => !cancelled && setDetailState({ status: "error", data: null, error, forId: selectedId }));
    return () => {
      cancelled = true;
    };
  }, [selectedId]);

  function handleAcknowledge() {
    setActionState({ status: "submitting", error: null });
    acknowledgeAbnormalEvent(selectedId)
      .then((data) => {
        setActionState({ status: "idle", error: null });
        setDetailState({ status: "ready", data, error: null, forId: selectedId });
        refetch();
      })
      .catch((error) => setActionState({ status: "error", error }));
  }

  function handleResolve() {
    setActionState({ status: "submitting", error: null });
    resolveAbnormalEvent(selectedId)
      .then((data) => {
        setActionState({ status: "idle", error: null });
        setDetailState({ status: "ready", data, error: null, forId: selectedId });
        refetch();
      })
      .catch((error) => setActionState({ status: "error", error }));
  }

  const items = listData?.items ?? [];
  const totalCount = listData?.totalCount ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / ABNORMAL_EVENTS_DEFAULT_PAGE_SIZE));

  return (
    <>
      <div className="wx-asa-toolbar">
        <label className="wx-asa-search">
          <span className="wx-visually-hidden">ค้นหาเหตุการณ์ Abnormal</span>
          <input type="search" placeholder="ค้นหารหัสเหตุการณ์, แรงงาน..." value={searchInput} onChange={(event) => setSearchInput(event.target.value)} />
        </label>
        <label className="wx-asa-filter">
          <span>สถานะ</span>
          <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
            <option value="">ทั้งหมด</option>
            <option value="Open">Open</option>
            <option value="Acknowledged">Acknowledged</option>
            <option value="Resolved">Resolved</option>
          </select>
        </label>
      </div>

      <section className="wx-panel wx-asa-list-panel">
        <div className="wx-asa-list-head">
          <h2 className="wx-panel-title">รายการ Abnormal Heartbeat</h2>
          <span className="wx-asa-list-count">{totalCount} เหตุการณ์</span>
        </div>

        {listStatus === "error" && listData === null ? (
          <div className="wx-state-card wx-state-card--danger" role="alert">
            <p className="wx-state-body">{describeListError(listError)}</p>
          </div>
        ) : listStatus === "loading" && listData === null ? (
          <PageSkeleton variant="table" label="กำลังโหลดรายการ Abnormal Heartbeat..." />
        ) : items.length === 0 ? (
          <p className="wx-empty-list">ไม่พบเหตุการณ์ Abnormal Heartbeat ในสายงานของคุณ</p>
        ) : (
          <>
            <div className="wx-asa-table-wrap">
              <table className="wx-asa-table">
                <thead>
                  <tr>
                    <th>เหตุการณ์</th>
                    <th>แรงงาน</th>
                    <th>ประเภท</th>
                    <th>ความรุนแรง</th>
                    <th>สถานะ</th>
                    <th>ตรวจพบเมื่อ</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => (
                    <tr key={item.abnormalEventId} className="wx-asa-row" onClick={() => setSelectedId(item.abnormalEventId)}>
                      <td>
                        <strong className="wx-asa-cell-strong">{item.abnormalCode ?? "-"}</strong>
                      </td>
                      <td>{item.workerName ?? item.workerCode ?? "-"}</td>
                      <td>{item.abnormalType ?? "-"}</td>
                      <td>
                        <span className={`wx-badge wx-badge--${severityBadgeVariant(item.severity)}`}>{item.severity ?? "-"}</span>
                      </td>
                      <td>
                        <span className={`wx-badge wx-badge--${statusBadgeVariant(item.status)}`}>{item.status}</span>
                      </td>
                      <td>{formatDateTimeTh(item.detectedAtUtc) ?? "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

              <Pagination
                page={page}
                totalPages={totalPages}
                onPageChange={setPage}
              />
          </>
        )}
      </section>

      <DetailDrawer open={selectedId !== null} onClose={() => setSelectedId(null)} labelledBy={DRAWER_TITLE_ID} selectableSelector="tr.wx-asa-row">
        <AbnormalDetailContent
          titleId={DRAWER_TITLE_ID}
          state={detailState}
          actionState={actionState}
          onAcknowledge={handleAcknowledge}
          onResolve={handleResolve}
        />
      </DetailDrawer>
    </>
  );
}

function AbnormalDetailContent({ titleId, state, actionState, onAcknowledge, onResolve }) {
  if (state.status === "loading" || state.status === "idle") {
    return (
      <DetailSkeleton label="กำลังโหลดรายละเอียด..." />
    );
  }
  if (state.status === "error") {
    return (
      <div className="wx-asa-drawer-state" role="alert">
        <h2 id={titleId} className="wx-state-title">
          ไม่สามารถโหลดรายละเอียดได้
        </h2>
      </div>
    );
  }

  const detail = state.data;

  return (
    <div className="wx-asa-drawer">
      <span className="wx-asa-drawer-kicker">ABNORMAL · DETAIL</span>
      <h2 id={titleId} className="wx-asa-drawer-title">
        {detail.abnormalCode ?? "-"}
      </h2>
      <p className="wx-asa-drawer-sub">{detail.workerName ?? detail.workerCode ?? "-"}</p>
      <div className="wx-asa-drawer-status">
        <span className={`wx-badge wx-badge--${statusBadgeVariant(detail.status)}`}>{detail.status}</span>
        <span className={`wx-badge wx-badge--${severityBadgeVariant(detail.severity)}`}>{detail.severity ?? "-"}</span>
      </div>

      <section className="wx-asa-dsec">
        <h3>รายละเอียด</h3>
        <div className="wx-asa-kv-row">
          <span>ประเภท</span>
          <strong>{detail.abnormalType ?? "-"}</strong>
        </div>
        <div className="wx-asa-kv-row">
          <span>นายจ้าง</span>
          <strong>{detail.employerName ?? "-"}</strong>
        </div>
        <div className="wx-asa-kv-row">
        </div>
        <div className="wx-asa-kv-row">
          <span>ตรวจพบเมื่อ</span>
          <strong>{formatDateTimeTh(detail.detectedAtUtc) ?? "-"}</strong>
        </div>
        <div className="wx-asa-kv-row">
          <span>Heartbeat ล่าสุด</span>
          <strong>{formatDateTimeTh(detail.currentLastHeartbeatAtUtc) ?? "ไม่เคยส่ง heartbeat"}</strong>
        </div>
        {detail.acknowledgedAtUtc && (
          <div className="wx-asa-kv-row">
            <span>รับทราบเมื่อ</span>
            <strong>{formatDateTimeTh(detail.acknowledgedAtUtc)}</strong>
          </div>
        )}
        {detail.resolvedAtUtc && (
          <div className="wx-asa-kv-row">
            <span>ปิดเหตุการณ์เมื่อ</span>
            <strong>{formatDateTimeTh(detail.resolvedAtUtc)}</strong>
          </div>
        )}
      </section>

      {actionState.status === "error" && (
        <p className="wx-asa-form-message" role="alert">
          {describeWriteError(actionState.error)}
        </p>
      )}

      <div className="wx-asa-drawer-actions">
        {detail.nextActions.canAcknowledge && (
          <button type="button" className="wx-button wx-button--ghost" onClick={onAcknowledge} disabled={actionState.status === "submitting"}>
            รับทราบ (Acknowledge)
          </button>
        )}
        {detail.nextActions.canResolve && (
          <button type="button" className="wx-button wx-button--primary" onClick={onResolve} disabled={actionState.status === "submitting"}>
            ปิดเหตุการณ์ (Resolve)
          </button>
        )}
      </div>
    </div>
  );
}
